import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Divider,
  CircularProgress,
  Button,
  IconButton,
  Alert,
  AlertTitle,
  Stack
} from '@mui/material';
import { useParams } from 'react-router-dom';
import api from '../../../services/api';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// Simple key-value row
const Row = ({ label, value }) => (
  <Box sx={{ mb: 1.25 }}>
    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a', wordBreak: 'break-word' }}>
      {value ?? '-'}
    </Typography>
  </Box>
);

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getYesNo = (val) => {
  if (val === true) return 'Ya';
  if (val === false) return 'Tidak';
  return '-';
};

const labelLanguageLevel = (c) => ({ S: 'Sedang', CB: 'Cukup Baik', B: 'Baik', SB: 'Sangat Baik' }[c] || (c || '-'));

// Format family type to proper Indonesian
const formatFamilyType = (type) => {
  const typeMap = {
    'parent_father': 'Ayah Kandung',
    'parent_mother': 'Ibu Kandung',
    'spouse': 'Suami/Istri',
    'sibling': 'Saudara Kandung',
    'child': 'Anak'
  };
  return typeMap[type] || type || '-';
};

// Compute last education summary from education list (same logic as Step7Review)
// const getLastEducation = (educationList) => {
//   const list = Array.isArray(educationList) ? educationList : [];
//   if (!list.length) {
//     return { institution: '-', major: '-', graduationYear: '-', level: '-' };
//   }
//   const getYear = (period) => {
//     if (!period) return 0;
//     const parts = String(period).split('-');
//     const last = parts[parts.length - 1];
//     const y = parseInt(last, 10);
//     return Number.isFinite(y) ? y : 0;
//   };
//   const sorted = [...list].sort((a, b) => getYear(b.period) - getYear(a.period));
//   const last = sorted[0] || {};
//   return {
//     institution: last.schoolName || '-',
//     major: last.major || '-',
//     graduationYear: last.period ? (String(last.period).split('-')[1] || last.period) : '-',
//     level: last.certificate || '-'
//   };
// };

// Format number to Indonesian Rupiah (Rp with thousand separators)
const formatRupiah = (v) => {
  if (v === null || v === undefined || v === '') return '-';

  const parseNumeric = (input) => {
    if (typeof input === 'number') return input;

    let cleaned = String(input).trim();
    if (!cleaned) return NaN;

    cleaned = cleaned.replace(/[^0-9.,-]/g, '');
    if (!cleaned) return NaN;

    const hasComma = cleaned.includes(',');
    const hasDot = cleaned.includes('.');

    if (hasComma && hasDot) {
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    } else if (hasComma) {
      cleaned = cleaned.replace(/,/g, '.');
    } else if (hasDot) {
      const lastDot = cleaned.lastIndexOf('.');
      const decimals = cleaned.length - lastDot - 1;
      const isDecimal = decimals > 0 && decimals <= 2;
      if (isDecimal) {
        const integerPart = cleaned.slice(0, lastDot).replace(/\./g, '');
        cleaned = `${integerPart}.${cleaned.slice(lastDot + 1)}`;
      } else {
        cleaned = cleaned.replace(/\./g, '');
      }
    }

    return Number(cleaned);
  };

  const numericValue = parseNumeric(v);
  if (!Number.isFinite(numericValue)) return '-';

  const options = Number.isInteger(numericValue)
    ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
    : { minimumFractionDigits: 2, maximumFractionDigits: 2 };

  return `Rp ${numericValue.toLocaleString('id-ID', options)}`;
};

// Format allowance yang bisa berisi angka dan text
const formatAllowance = (v) => {
  if (v === null || v === undefined || v === '') return '-';
  
  const stringValue = String(v);
  
  // Cek apakah ada angka di dalam text
  const numericParts = stringValue.match(/\d+/g);
  
  if (numericParts && numericParts.length > 0) {
    // Format setiap angka yang ditemukan dalam text
    let formattedValue = stringValue;
    numericParts.forEach(numStr => {
      // Format angka dengan reverse method untuk hasil yang benar
      const reversed = numStr.split('').reverse().join('');
      const formattedNum = reversed.match(/.{1,3}/g).join('.').split('').reverse().join('');
      if (numStr !== formattedNum) {
        formattedValue = formattedValue.split(numStr).join(formattedNum);
      }
    });
    return formattedValue;
  } else {
    // Jika tidak ada angka sama sekali, kembalikan asli
    return stringValue;
  }
};

// Format date from YYYY-MM-DD to DD Month YYYY
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '-';
  
  const day = date.getDate();
  const monthNames = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 
                     'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  
  return `${day} ${month} ${year}`;
};

// Calculate age from birth date
const calculateAge = (birthDateString) => {
  if (!birthDateString) return '-';
  const birthDate = new Date(birthDateString);
  if (isNaN(birthDate.getTime())) return '-';
  
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return `${age} Tahun`;
};

// Section wrapper styled similar to Step7Review
const SectionCard = ({ title, children, actions }) => (
  <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, backgroundColor: '#ffffff', mb: 2.5, borderRadius: 2, borderColor: 'divider' }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a' }}>{title}</Typography>
      {actions}
    </Box>
    {children}
  </Paper>
);

const AdminRecruitmentDetail = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({});

  const handleDownloadDocument = async (documentId, fileName) => {
    try {
      setDownloading((prev) => ({ ...prev, [documentId]: true }));
      const response = await api.get(`/recruitment/applications/${id}/documents/${documentId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Gagal mengunduh dokumen. Silakan coba lagi.');
    } finally {
      setDownloading((prev) => ({ ...prev, [documentId]: false }));
    }
  };

  useEffect(() => {
    const run = async () => {
      try {
        const res = await api.get(`/recruitment/applications/${id}`);
        setData(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [id]);

  if (loading) return (
    <Box sx={{ p: 3, textAlign: 'center' }}>
      <CircularProgress />
    </Box>
  );
  if (!data) return <Box sx={{ p: 3 }}>Data tidak ditemukan</Box>;

  const photoDoc = Array.isArray(data?.documents)
    ? (data.documents.find((d) => d.type === 'pas_foto') || data.documents.find((d) => (d.mimeType || '').includes('image')))
    : null;
  const photoUrl = photoDoc?.url
    ? (photoDoc.url.startsWith('http')
      ? photoDoc.url
      : `${process.env.REACT_APP_API_URL || ''}${photoDoc.url.replace(/^\/api\//, '/')}`)
    : null;

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', p: { xs: 1.5, sm: 2.5, md: 3 }, bgcolor: '#F8FAFC' }}>
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, sm: 2.5 },
          mb: 3,
          borderRadius: 2,
          borderColor: 'divider',
          backgroundColor: '#ffffff'
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>
              DETAIL APLIKASI REKRUTMEN
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ringkasan lengkap data kandidat untuk proses review admin.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => window.history.back()}
            >
              Kembali
            </Button>
            <Button
              variant="contained"
              startIcon={<PictureAsPdfIcon />}
              onClick={() => window.open(`${process.env.REACT_APP_API_URL || ''}/recruitment/applications/${id}/pdf`, '_blank')}
              sx={{ bgcolor: '#0f172a', '&:hover': { bgcolor: '#0b1324' } }}
            >
              Download PDF
            </Button>
          </Stack>
        </Stack>

        {photoUrl && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Box
              component="img"
              src={photoUrl}
              alt="Pas Foto"
              sx={{
                width: 120,
                height: 150,
                objectFit: 'cover',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1
              }}
            />
          </Box>
        )}

        <Alert severity="info" sx={{ mt: 2 }}>
          <AlertTitle>Informasi</AlertTitle>
          Dokumen kandidat dapat diunduh di bagian akhir halaman.
        </Alert>
      </Paper>

      {/* Data Pribadi */}
      <SectionCard title="DATA PRIBADI">
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Row label="Nama Lengkap" value={data.fullName} />
            <Row label="Nama Panggilan" value={data.nickname} />
            <Row label="Tempat, Tanggal Lahir" value={`${data.birthPlace || '-'}, ${formatDate(data.birthDate)}`} />
            <Row label="Usia" value={calculateAge(data.birthDate)} />
            <Row label="Kewarganegaraan/Suku Bangsa" value={data.nationality} />
            <Row label="Agama" value={data.religion} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Row label="Status Pernikahan" value={data.maritalStatus} />
            <Row label="Jenis Kelamin" value={data.gender} />
            <Row label="Golongan Darah" value={data.bloodType} />
            <Row label="Buta Warna" value={data.colorBlind} />
            <Row label="Tinggi / Berat Badan" value={`${data.heightCm || '-'} cm / ${data.weightKg || '-'} kg`} />
          </Grid>
          <Grid item xs={12}>
            <Row label="Alamat KTP" value={data.addressKtp} />
            <Row label="Alamat Domisili" value={data.addressCurrent} />
            <Row label="No. Telepon" value={data.phone} />
            <Row label="Email" value={data.email} />
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>KONTAK DARURAT:</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Row label="Nama Lengkap" value={data.emergencyContactName} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Row label="No. HP" value={data.emergencyContactPhone} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Row label="Hubungan" value={data.emergencyContactRelation} />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>INFORMASI TAMBAHAN:</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Row label="Hobby" value={data.hobby} />
                <Row label="Kelebihan" value={data.selfPositivePoints} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Row label="Kelemahan" value={data.selfWeaknessPoints} />
                <Row label="Inovasi" value={data.innovationDescription} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </SectionCard>

      {/* Pendidikan */}
      <SectionCard title="DATA PENDIDIKAN">
        <Grid container spacing={2}>
          {/* 1. Riwayat Pendidikan Formal */}
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>1. RIWAYAT PENDIDIKAN FORMAL:</Typography>
            {Array.isArray(data.education) && data.education.length > 0 ? (
              <Grid container spacing={1}>
                {data.education.map((edu, idx) => (
                  <Grid item xs={12} key={idx}>
                    <Box sx={{ 
                      p: 2, 
                      backgroundColor: '#ffffff', 
                      borderRadius: 1, 
                      border: '1px solid #e0e0e0',
                      mb: 1 
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: '#1976d2' }}>
                        Nama Sekolah: {edu.schoolName || '-'}
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={12} md={2}>
                          <Row label="1. Periode" value={edu.period || '-'} />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <Row label="3. Jurusan" value={edu.major || '-'} />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <Row label="4. IPK" value={edu.grade || '-'} />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <Row label="5. Kota" value={edu.city || '-'} />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <Row label="6. Ijasah" value={edu.certificate || '-'} />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2">-</Typography>
            )}
          </Grid>
          
          {/* 2. Kursus / Pelatihan yang pernah dan sedang diikuti (Attended Courses / Training) */}
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>2. KURSUS / PELATIHAN YANG PERNAH DAN SEDANG DIIKUTI (ATTENDED COURSES / TRAINING):</Typography>
            {Array.isArray(data.courses) && data.courses.length > 0 ? (
              <Grid container spacing={1}>
                {data.courses.map((course, idx) => (
                  <Grid item xs={12} key={idx}>
                    <Box sx={{ 
                      p: 2, 
                      backgroundColor: '#ffffff', 
                      borderRadius: 1, 
                      border: '1px solid #e0e0e0',
                      mb: 1 
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: '#1976d2' }}>
                        Nama Kursus: {course.courseName || '-'}
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={12} md={4}>
                          <Row label="Penyelenggara" value={course.organizer || '-'} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Row label="Periode" value={course.period || '-'} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Row label="Sertifikat" value={course.certificate || '-'} />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2">-</Typography>
            )}
          </Grid>
        </Grid>
      </SectionCard>

      {/* Pengalaman Kerja */}
      <SectionCard title="DATA PENGALAMAN KERJA">
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            {/* Fields moved to DATA PRIBADI section */}
          </Grid>
          <Grid item xs={12} md={6}>
            
            {/* <Row label="Portfolio URL" value={data.portfolioUrl} /> */}
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>RIWAYAT PEKERJAAN:</Typography>
            {Array.isArray(data.workHistory) && data.workHistory.length > 0 ? (
              data.workHistory.map((work, idx) => (
                <Box key={idx} sx={{ mb: 2, p: 2, backgroundColor: '#ffffff', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {idx + 1}. {work.companyName || '-'}
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={6}>
                      <Row label="Tahun Kerja" value={work.period || '-'} />
                      <Row label="Jabatan" value={work.position || '-'} />
                      <Row label="Gaji Pokok" value={formatRupiah(work.basicSalary)} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Row label="Tunjangan" value={formatAllowance(work.allowance)} />
                      <Row label="Alasan Pindah" value={work.movingReason || '-'} />
                      <Row label="Uraian Pekerjaan/Tugas Pokok" value={work.jobDescription} />
                    </Grid>
                  </Grid>
                </Box>
              ))
            ) : (
              <Typography variant="body2">-</Typography>
            )}
          </Grid>
        </Grid>
      </SectionCard>

      {/* Bahasa */}
      <SectionCard title="BAHASA">
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Row label="Skill Presentasi" value={data.presentationSkills} />
            <Row label="Skill Negosiasi" value={data.negotiationSkills} />
            <Row label="Skill Menulis" value={data.writingSkills} />
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Grid container spacing={2}>
              {Array.isArray(data.languages) && data.languages.length > 0 ? (
                data.languages.map((lang, idx) => (
                  <Grid item xs={12} key={idx}>
                    <Box sx={{ backgroundColor: '#f8f9fa', p: 2, borderRadius: 1, border: '1px solid #e0e0e0', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: '#1976d2' }}>
                        {lang.language || '-'}
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={12} md={6}>
                          <Row label="Kemampuan Tertulis" value={labelLanguageLevel(lang.written)} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Row label="Kemampuan Lisan" value={labelLanguageLevel(lang.oral)} />
                        </Grid>
                      </Grid>
                      {lang.remark && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          <strong>Keterangan:</strong> {lang.remark}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}><Typography variant="body2">-</Typography></Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </SectionCard>

      {/* Data Keluarga */}
      <SectionCard title="DATA KELUARGA">
        {Array.isArray(data.family) && data.family.length > 0 ? (
          <Grid container spacing={2}>
            {data.family.map((f, idx) => (
              <Grid item xs={12} key={idx}>
                <Box sx={{ 
                  p: 2, 
                  backgroundColor: '#ffffff', 
                  borderRadius: 1, 
                  border: '1px solid #e0e0e0',
                  mb: 1 
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: '#1976d2' }}>
                    {formatFamilyType(f.type)}
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={3}>
                      <Row label="Usia" value={f.age || '-'} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Row label="Pendidikan" value={f.education || '-'} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Row label="Pekerjaan" value={f.job || '-'} />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography variant="body2">-</Typography>
        )}
      </SectionCard>

      {/* Referensi */}
      <SectionCard title="REFERENSI">
        <Row label="Referensi 1" value={`${data.reference1Name || '-'} - ${data.reference1Position || '-'}`} />
        <Row label="Telepon 1" value={data.reference1Phone} />
        <Row label="Email 1" value={data.reference1Email} />
        <Row label="Referensi 2" value={`${data.reference2Name || '-'} - ${data.reference2Position || '-'}`} />
        <Row label="Telepon 2" value={data.reference2Phone} />
        <Row label="Email 2" value={data.reference2Email} />
      </SectionCard>

      {/* Penghargaan */}
      <SectionCard title="PENGHARGAAN">
        <Row label="Penghargaan 1" value={data.award1} />
        <Row label="Penghargaan 2" value={data.award2} />
        <Row label="Penghargaan 3" value={data.award3} />
      </SectionCard>

      {/* Organisasi */}
      <SectionCard title="ORGANISASI">
        <Row label="Organisasi 1" value={`${data.organization1Name || '-'} - ${data.organization1Position || '-'} (${data.organization1Period || '-'})`} />
        <Row label="Organisasi 2" value={`${data.organization2Name || '-'} - ${data.organization2Position || '-'} (${data.organization2Period || '-'})`} />
        <Row label="Organisasi 3" value={`${data.organization3Name || '-'} - ${data.organization3Position || '-'} (${data.organization3Period || '-'})`} />
      </SectionCard>


      {/* Gaji & Benefits */}
      <SectionCard title="GAJI & BENEFITS">
        <Row label="Gaji yang Diharapkan" value={formatRupiah(data.expectedSalary)} />
        <Row label="Tunjangan & Fasilitas yang Diinginkan" value={data.benefitsWanted} />
      </SectionCard>

      {/* Pertanyaan Tambahan */}
      <SectionCard title="PERTANYAAN TAMBAHAN">
        <Row label="Boleh Hubungi Institusi" value={getYesNo(data.allowContactInstitution)} />
        <Row label="Punya Kerabat di Perusahaan" value={getYesNo(data.hasRelativesInCompany)} />
        <Row label="Info Kerabat" value={data.relativesInfo} />
        <Row label="Punya Disabilitas Fisik" value={getYesNo(data.hasPhysicalDisabilities)} />
        <Row label="Info Disabilitas" value={data.disabilitiesInfo} />
        <Row label="Punya Penyakit Serius" value={getYesNo(data.hasSeriousIllness)} />
        <Row label="Info Penyakit" value={data.illnessInfo} />
        <Row label="Rencana Lanjut Pendidikan" value={getYesNo(data.planContinueEducation)} />
        <Row label="Info Pendidikan" value={data.educationPlanInfo} />
        <Row label="Alasan Join Perusahaan" value={data.whyJoinCompany} />
        <Row label="Bekerja di Luar Expertise" value={getYesNo(data.willingWorkBeyondExpertise)} />
        <Row label="Alasan Beyond Expertise" value={data.beyondExpertiseReason} />
        <Row label="Kapan Bisa Mulai" value={data.whenCanStart} />
        <Row label="Pengetahuan Perusahaan" value={data.companyKnowledge} />
        <Row label="Additional Info" value={data.additionalInfo} />
        <Row label="Job Source" value={data.jobSource} />
        <Row label="Position Expectations" value={data.positionExpectations} />
      </SectionCard>

      {/* Dokumen */}
      <SectionCard title="DOKUMEN">
        {Array.isArray(data.documents) && data.documents.length > 0 ? (
          <Grid container spacing={2}>
            {data.documents.map((d) => (
              <Grid item xs={12} md={6} key={d.id}>
                <Box
                  sx={{
                    p: 2,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    backgroundColor: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {d.mimeType?.includes('pdf') ? (
                      <PictureAsPdfIcon sx={{ color: '#d32f2f', fontSize: 24 }} />
                    ) : d.mimeType?.includes('image') ? (
                      <ImageIcon sx={{ color: '#1976d2', fontSize: 24 }} />
                    ) : (
                      <InsertDriveFileIcon sx={{ color: '#757575', fontSize: 24 }} />
                    )}
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {d.type || 'Dokumen'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        {d.name} ({formatFileSize(d.size || 0)})
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton
                    onClick={() => handleDownloadDocument(d.id, d.name)}
                    color="primary"
                    size="small"
                    disabled={downloading[d.id]}
                    sx={{
                      backgroundColor: downloading[d.id] ? '#f5f5f5' : '#e3f2fd',
                      '&:hover': {
                        backgroundColor: downloading[d.id] ? '#f5f5f5' : '#bbdefb'
                      }
                    }}
                  >
                    {downloading[d.id] ? <CircularProgress size={20} /> : <DownloadIcon />}
                  </IconButton>
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic' }}>
            Tidak ada dokumen yang diupload
          </Typography>
        )}
      </SectionCard>
    </Box>
  );
};

export default AdminRecruitmentDetail;
